<template>
  <div class="p-4 max-w-[1600px] mx-auto">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">Audit Logs</h1>
        <p class="text text-gray-600 dark:text-gray-400">Track user actions across the platform</p>
      </div>
      <div class="flex table-buttons-row">
        <div v-if="isLoading === true && rows.length === 0" class="flex table-buttons-row">
          <div class="h-10 w-64 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
          <div class="h-10 w-24 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
        </div>
        <div v-else class="flex table-buttons-row">
          <input v-model="search" type="text" placeholder="Search user or path" class="input input-bordered" />
          <button class="btn" @click="loadLogs" :disabled="isLoading === true">Apply</button>
        </div>
      </div>
    </div>

    <div class="card shadow bg-white dark:bg-gray-800 rounded-md p-4 border dark:border-gray-700 mb-6">
      <div v-if="isLoading === true && rows.length === 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 items-end search-fields-grid">
        <div v-for="n in 6" :key="n" class="animate-pulse">
          <div class="h-4 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div class="mt-2 h-10 w-full rounded-md bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 items-end search-fields-grid">
        <div>
          <label class="label"><span class="label-text">User ID</span></label>
          <input v-model="filters.userId" type="number" class="input input-bordered w-full" />
        </div>
        <div>
          <label class="label"><span class="label-text">Method</span></label>
          <select v-model="filters.method" class="select select-bordered w-full">
            <option value="">All</option>
            <option>GET</option>
            <option>POST</option>
            <option>PUT</option>
            <option>DELETE</option>
          </select>
        </div>
        <div>
          <label class="label"><span class="label-text">Status</span></label>
          <input v-model="filters.statusCode" type="number" class="input input-bordered w-full" />
        </div>
        <div>
          <label class="label"><span class="label-text">Success</span></label>
          <select v-model="filters.success" class="select select-bordered w-full">
            <option value="">All</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
        </div>
        <div>
          <label class="label"><span class="label-text">From</span></label>
          <input v-model="filters.dateFrom" type="datetime-local" class="input input-bordered w-full" />
        </div>
        <div>
          <label class="label"><span class="label-text">To</span></label>
          <input v-model="filters.dateTo" type="datetime-local" class="input input-bordered w-full" />
        </div>
      </div>
    </div>

    <div class="card shadow bg-white dark:bg-gray-800 rounded-md p-4 border dark:border-gray-700">
      <div v-if="isLoading === true && rows.length === 0" class="flex justify-between items-center mb-4">
        <div class="h-10 w-56 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
        <div class="flex">
          <div class="h-10 w-20 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
          <div class="ml-2 h-10 w-20 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
        </div>
      </div>
      <div v-else class="flex justify-between items-center mb-4">
        <div class="form-control">
          <input v-model="search" type="text" placeholder="Filter table" class="input input-bordered" />
        </div>
        <div>
          <button class="btn btn-outline" @click="prevPage" :disabled="offset === 0 || isLoading === true">Prev</button>
          <button class="btn btn-outline ml-2" @click="nextPage" :disabled="(offset + limit) >= total || isLoading === true">Next</button>
        </div>
      </div>
      
      <div v-if="isLoading === true && rows.length === 0" class="animate-pulse">
        <div class="w-full overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
          <div class="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 p-4">
            <div class="h-4 w-64 rounded bg-gray-200 dark:bg-gray-600"></div>
          </div>
          <div class="p-4">
            <div v-for="n in 10" :key="`audit-skel-${n}`" class="flex items-center border-b border-gray-200 dark:border-gray-700 py-3">
              <div class="h-3 w-28 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="ml-4 h-3 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="ml-4 h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="ml-4 h-3 w-80 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="ml-4 h-3 w-14 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="ml-4 h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="ml-4 h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="ml-4 h-3 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="ml-4 h-3 w-40 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <ThemeOnly theme="modern" :invert="true">
          <EasyDataTable :headers="headers" :items="rows" :loading="isLoading === true" :search-value="search" :search-field="['username','method','path','statuscode']" table-class-name="enterprise-table" buttons-pagination>
            <template #item-createdat="{ createdat }">{{ formatDate(createdat) }}</template>
            <template #item-success="{ success }">
              <span :class="['badge', success === true ? 'badge-success' : 'badge-error']">{{ success === true ? 'Yes' : 'No' }}</span>
            </template>
            <template #item-method="{ method }"><span class="badge badge-outline">{{ method }}</span></template>
            <template #item-path="{ path }"><div class="max-w-lg truncate" :title="path">{{ path }}</div></template>
            <template #item-statuscode="{ statuscode }"><span :class="['badge', statusColor(statuscode)]">{{ statuscode }}</span></template>
            <template #loading>
              <div class="p-6 animate-pulse">
                <div v-for="n in 8" :key="`audit-load-${n}`" class="flex items-center border-b border-gray-200 dark:border-gray-700 py-3">
                  <div class="h-3 w-28 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="ml-4 h-3 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="ml-4 h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="ml-4 h-3 w-80 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="ml-4 h-3 w-14 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="ml-4 h-3 w-16 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="ml-4 h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="ml-4 h-3 w-24 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div class="ml-4 h-3 w-40 rounded bg-gray-200 dark:bg-gray-700"></div>
                </div>
              </div>
            </template>
          </EasyDataTable>
        </ThemeOnly>

        <ThemeOnly theme="modern">
          <ModernAgentTable 
            :headers="headers" 
            :items="rows" 
            :loading="isLoading === true"
          >
            <template #item-createdat="{ item }">{{ formatDate(item.createdat) }}</template>
            <template #item-success="{ item }">
              <span class="status-pill" :class="item.success === true ? 'status-active' : 'status-disabled'">
                {{ item.success === true ? 'Yes' : 'No' }}
              </span>
            </template>
            <template #item-method="{ item }">
              <span class="type-pill type-public uppercase text-[10px]">{{ item.method }}</span>
            </template>
            <template #item-path="{ item }">
              <div class="max-w-xs truncate font-mono text-[10px]" :title="item.path">{{ item.path }}</div>
            </template>
            <template #item-statuscode="{ item }">
              <span :class="['badge badge-sm', statusColor(item.statuscode)]">{{ item.statuscode }}</span>
            </template>
          </ModernAgentTable>
        </ThemeOnly>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import router from '@/router';
import ModernLoader from '@/components/ModernLoader.vue';
import ThemeOnly from '@/components/ThemeOnly.vue';
import ModernAgentTable from '@/components/ModernAgentTable.vue';
import { Icon } from '@iconify/vue';

const toast = useToast();
const api = globalState.apiEndpoint;

const headers = [
  { text: 'Time', value: 'createdat' },
  { text: 'User', value: 'username' },
  { text: 'UserId', value: 'userid' },
  { text: 'Method', value: 'method' },
  { text: 'Path', value: 'path' },
  { text: 'Status', value: 'statuscode' },
  { text: 'Success', value: 'success' },
  { text: 'IP', value: 'ip' },
  { text: 'Agent', value: 'useragent' }
];

const rows = ref([]);
const total = ref(0);
const limit = ref(50);
const offset = ref(0);
const isLoading = ref(false);
const search = ref('');
const filters = ref({ userId: '', method: '', statusCode: '', success: '', dateFrom: '', dateTo: '' });

function getAuthHeaders() {
  return {};
}

function formatDate(val) {
  if (val === undefined || val === null || String(val) === '') return '';
  const d = new Date(val);
  if (isNaN(d.getTime()) === true) return String(val);
  return d.toLocaleString();
}

function statusColor(code) {
  if (typeof code !== 'number') return 'badge-neutral';
  if (code >= 200 && code < 300) return 'badge-success';
  if (code >= 400 && code < 500) return 'badge-warning';
  if (code >= 500) return 'badge-error';
  return 'badge-ghost';
}

async function loadLogs() {
  isLoading.value = true;
  try {
    const params = { limit: limit.value, offset: offset.value };
    if (filters.value.userId !== '') params.userId = filters.value.userId;
    if (filters.value.method !== '') params.method = filters.value.method;
    if (filters.value.statusCode !== '') params.statusCode = filters.value.statusCode;
    if (filters.value.success !== '') params.success = filters.value.success;
    if (filters.value.dateFrom !== '') params.dateFrom = new Date(filters.value.dateFrom).toISOString();
    if (filters.value.dateTo !== '') params.dateTo = new Date(filters.value.dateTo).toISOString();
    if (search.value !== '') params.search = search.value;
    const res = await axios.get(`${api}/api/audit/`, { withCredentials: true, headers: getAuthHeaders(), params });
    total.value = res.data.total;
    rows.value = res.data.items.map(r => ({
      ...r,
      createdat: r.createdat,
      username: r.username,
      userid: r.userid,
      method: r.method,
      path: r.path,
      statuscode: r.statuscode,
      success: r.success,
      ip: r.ip,
      useragent: r.useragent
    }));
  } catch (e) {
    toast.error('Failed to load audit logs');
    if (e?.response?.status === 401 || e?.response?.status === 403) router.push('/login');
  } finally {
    isLoading.value = false;
  }
}

function nextPage() { if ((offset.value + limit.value) < total.value) { offset.value += limit.value; loadLogs(); } }
function prevPage() { if (offset.value > 0) { offset.value = Math.max(0, offset.value - limit.value); loadLogs(); } }

onMounted(() => { loadLogs(); });
</script>

<style scoped>
.enterprise-table {
  @apply w-full;
}
.table-buttons-row {
  margin: -0.5rem;
}
.table-buttons-row > * {
  margin: 0.5rem;
}
.search-fields-grid {
  margin: -0.5rem;
}
.search-fields-grid > * {
  margin: 0.5rem;
}
</style>


